// Volcanoes — render-only crater, lava pool, ash plume, embers / lava bombs
// and a night glow wash on the neighbouring tile tops. Identity and state
// (dormant / active / erupting) come from volcano.ts (placeholder derivation
// plus the explicit `volcano` tile prop; see that file). Only drawn on
// levelled boards. The land-tile shader (volcanoTileShader.ts) handles the
// ash flanks / no-snow / fissure glow on the tile itself.
//
// Perf (renderer-crash-rca rules): FIVE draw calls total regardless of
// volcano count — rock cone, lava disc, plume puffs, embers, glow — four
// InstancedMeshes over shared module-level geometry plus ONE preallocated
// glow mesh (volcanoGlow.ts, surface-following fans), each with a shared
// ShaderMaterial. Per-instance state is in `instanceColor` (written once per
// derivation); animation is entirely the shared `uTime` uniform. No lights,
// no per-frame allocation. Honors prefers-reduced-motion (time frozen, flash
// off via uMotion) and the particle-effects setting (plume/embers dropped,
// time frozen), like the waterfalls.

import type { SceneNode } from '@aod/protocol';
import { useFrame } from '@react-three/fiber';
import { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useSettings } from '../settings/useSettings.js';
import { dayNightState, nightFactor } from './DayNightLighting.js';
import { prefersReducedMotion } from './Waterfalls.js';
import { reliefAt, terrainLevelsActive, tileSurfaceY } from './elevation.js';
import { VOLCANO_CAP, type Volcano, deriveVolcanoes, volcanoLevel } from './volcano.js';
import {
  GLOW_PER_VOLCANO,
  type GlowBuffers,
  createGlowGeometry,
  fillGlowGeometry,
} from './volcanoGlow.js';
import { EMBER_FRAG, EMBER_VERT, PLUME_FRAG, PLUME_VERT } from './volcanoParticleShaders.js';
import {
  GLOW_FRAG,
  GLOW_VERT,
  LAVA_FRAG,
  LAVA_VERT,
  ROCK_FRAG,
  ROCK_VERT,
} from './volcanoShaders.js';

const PUFFS = 14;
const EMBERS = 8;
/** Erupting volcanoes throw twice as many sparks (every 4th is a bigger bomb). */
const EMBERS_ERUPT = 16;
/** Rim radius / cone base radius / rim height above the mountain peak. */
const RIM_R = 0.3;
const BASE_R = 0.62;
const RIM_LIFT = 0.1;
const LAVA_DROP = 0.07;

type Kind = 'rock' | 'lava' | 'plume' | 'ember';
type Meshes = Record<Kind, THREE.InstancedMesh | null>;

const uTime = { value: 0 };
const uNight = { value: 0 };
/** 1 while time runs, 0 when frozen (reduced motion / particles off): gates the crater flash. */
const uMotion = { value: 1 };

const rockGeo = new THREE.CylinderGeometry(RIM_R, BASE_R, 1, 14, 1, true).translate(0, 0.5, 0);
const lavaGeo = new THREE.CircleGeometry(1, 20).rotateX(-Math.PI / 2);
const quadGeo = new THREE.PlaneGeometry(1, 1);
// Glow wash: one preallocated surface-following mesh (volcanoGlow.ts).
const glowBuffers: GlowBuffers = createGlowGeometry(VOLCANO_CAP * GLOW_PER_VOLCANO);

function shader(vert: string, frag: string, opts: THREE.ShaderMaterialParameters = {}) {
  return new THREE.ShaderMaterial({
    uniforms: { uTime, uNight, uMotion },
    vertexShader: vert,
    fragmentShader: frag,
    ...opts,
  });
}
const soft = { transparent: true, depthWrite: false } as const;
const additive = { ...soft, blending: THREE.AdditiveBlending } as const;
const rockMat = shader(ROCK_VERT, ROCK_FRAG, { side: THREE.DoubleSide });
const lavaMat = shader(LAVA_VERT, LAVA_FRAG, { polygonOffset: true, polygonOffsetFactor: -1 });
const plumeMat = shader(PLUME_VERT, PLUME_FRAG, soft);
const emberMat = shader(EMBER_VERT, EMBER_FRAG, additive);
const glowMat = shader(GLOW_VERT, GLOW_FRAG, {
  ...additive,
  side: THREE.DoubleSide,
  polygonOffset: true,
  polygonOffsetFactor: -2,
});

const _o = new THREE.Object3D();
const centre = (q: number, r: number): [number, number] => [Math.sqrt(3) * (q + r / 2), 1.5 * r];

/** Append-cursor over one InstancedMesh (matrix + instanceColor). */
class Slot {
  n = 0;
  constructor(readonly mesh: THREE.InstancedMesh | null) {}

  put(
    pos: readonly [number, number, number],
    sx: number,
    sy: number,
    c: readonly [number, number, number],
  ) {
    const m = this.mesh;
    if (!m) return;
    _o.position.set(pos[0], pos[1], pos[2]);
    _o.scale.set(sx, sy, sx);
    _o.updateMatrix();
    m.setMatrixAt(this.n, _o.matrix);
    const a = m.instanceColor?.array as Float32Array | undefined;
    if (a) a.set(c, this.n * 3);
    this.n++;
  }

  finish() {
    const m = this.mesh;
    if (!m) return;
    m.count = this.n;
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
  }
}

/** Write every instance buffer once per derivation. */
export function fillVolcanoInstances(
  vols: readonly Volcano[],
  biomeMap: Map<string, string>,
  m: Meshes,
  withParticles: boolean,
): void {
  const rock = new Slot(m.rock);
  const lava = new Slot(m.lava);
  const plume = new Slot(m.plume);
  const ember = new Slot(m.ember);
  for (const v of vols) {
    const [x, z] = centre(v.q, v.r);
    const surface = tileSurfaceY(v.q, v.r, biomeMap);
    const rimY = surface + reliefAt('mountain', 0, 0) + RIM_LIFT;
    const baseY = surface + 0.1;
    const lvl = volcanoLevel(v.state);
    const seed = ((((v.q * 7919 + v.r * 104729) % 997) + 997) % 997) / 997;
    rock.put([x, baseY, z], 1, rimY - baseY, [lvl, seed, 0]);
    lava.put([x, rimY - LAVA_DROP, z], RIM_R * 0.92, 1, [lvl, seed, 0]);
    if (withParticles) {
      for (let i = 0; i < PUFFS; i++) plume.put([x, rimY, z], 0.6, 1, [lvl, seed, i / PUFFS]);
      const sparks = lvl > 1 ? EMBERS_ERUPT : lvl > 0 ? EMBERS : 0;
      for (let i = 0; i < sparks; i++) {
        // Erupting: every 4th spark is a bigger lava bomb.
        const size = lvl > 1 && i % 4 === 0 ? 0.07 : 0.035;
        ember.put([x, rimY - 0.02, z], size, 1, [lvl, seed, i / sparks]);
      }
    }
  }
  for (const s of [rock, lava, plume, ember]) s.finish();
  fillGlowGeometry(glowBuffers, vols, biomeMap);
}

function Instanced({
  name,
  geo,
  mat,
  capacity,
  order,
  forward,
}: {
  readonly name: string;
  readonly geo: THREE.BufferGeometry;
  readonly mat: THREE.ShaderMaterial;
  readonly capacity: number;
  readonly order: number;
  readonly forward: (m: THREE.InstancedMesh | null) => void;
}) {
  const data = useMemo(() => new Float32Array(capacity * 3), [capacity]);
  return (
    <instancedMesh
      ref={forward}
      name={name}
      args={[geo, mat, capacity]}
      frustumCulled={false}
      raycast={() => null}
      renderOrder={order}
    >
      <instancedBufferAttribute attach="instanceColor" args={[data, 3]} />
    </instancedMesh>
  );
}

export function Volcanoes({
  tiles,
  biomeMap,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly biomeMap: Map<string, string>;
}) {
  const particleEffects = useSettings((s) => s.particleEffects);
  const reduced = useMemo(prefersReducedMotion, []);
  const refs = useRef<Meshes>({ rock: null, lava: null, plume: null, ember: null });
  // Levels-gated: unlevelled boards carry no `level`, so nothing derives.
  const levelled = terrainLevelsActive();
  const vols = useMemo(() => (levelled ? deriveVolcanoes(tiles) : []), [tiles, levelled]);

  useLayoutEffect(() => {
    fillVolcanoInstances(vols, biomeMap, refs.current, particleEffects);
  }, [vols, biomeMap, particleEffects]);

  useFrame((_, delta) => {
    if (vols.length === 0) return;
    const moving = !reduced && particleEffects;
    uMotion.value = moving ? 1 : 0;
    if (moving) uTime.value += delta;
    uNight.value = nightFactor(dayNightState.sunHeight);
  });

  if (vols.length === 0) return null;
  const n = VOLCANO_CAP;
  const set = (k: Kind) => (m: THREE.InstancedMesh | null) => {
    refs.current[k] = m;
  };
  return (
    <group name="volcanoes">
      <Instanced
        name="volcano-rock"
        geo={rockGeo}
        mat={rockMat}
        capacity={n}
        order={3}
        forward={set('rock')}
      />
      <Instanced
        name="volcano-lava"
        geo={lavaGeo}
        mat={lavaMat}
        capacity={n}
        order={4}
        forward={set('lava')}
      />
      <mesh
        name="volcano-glow"
        geometry={glowBuffers.geometry}
        material={glowMat}
        frustumCulled={false}
        raycast={() => null}
        renderOrder={5}
      />
      {particleEffects && (
        <>
          <Instanced
            name="volcano-plume"
            geo={quadGeo}
            mat={plumeMat}
            capacity={n * PUFFS}
            order={6}
            forward={set('plume')}
          />
          <Instanced
            name="volcano-embers"
            geo={quadGeo}
            mat={emberMat}
            capacity={n * EMBERS_ERUPT}
            order={7}
            forward={set('ember')}
          />
        </>
      )}
    </group>
  );
}
